import { create } from 'zustand';

import { randomId } from '@/lib/random';

import type { QrBboxData } from '../types';

/**
 * A scanned/uploaded page that did NOT come back as a clean `success` and is
 * therefore HELD LOCALLY in the browser — never sent to the server until the
 * operator resolves it. This is the in-flight "needs action" queue for the
 * photo-scanning execution tab.
 *
 * Lifecycle of `resolution`:
 *   - `pending`   — captured but the operator hasn't fixed it yet.
 *   - `resolved`  — re-scanned / re-uploaded and now decodes as a clean QR
 *                   that matches the container; submitted WITH its QR.
 *   - `accepted`  — operator manually assigned a page number; submitted
 *                   WITHOUT a QR (honestly marked no-QR via expectedSequence).
 */
export interface QuarantineItem {
  localId: string;
  blob: Blob;
  /** Object URL for the thumbnail — REVOKED on replace / remove / clear. */
  objectUrl: string;
  qrData: string | null;
  qrBbox: QrBboxData | null;
  classification: 'mismatched' | 'unverified';
  resolution: 'pending' | 'resolved' | 'accepted';
  /** Set only when `resolution === 'accepted'`. */
  assignedPageNumber?: number;
}

/** Shape callers pass to `addItems` — the store mints `localId` + `objectUrl`. */
export interface QuarantineItemInput {
  blob: Blob;
  qrData: string | null;
  qrBbox: QrBboxData | null;
  classification: 'mismatched' | 'unverified';
}

interface QuarantineState {
  items: QuarantineItem[];
  addItems: (inputs: QuarantineItemInput[]) => void;
  /**
   * Patch a held item. When the patch carries a fresh `blob`, the old object
   * URL is revoked and a new one minted so the thumbnail tracks the new bytes.
   */
  updateItem: (
    localId: string,
    patch: Partial<
      Pick<
        QuarantineItem,
        | 'blob'
        | 'qrData'
        | 'qrBbox'
        | 'classification'
        | 'resolution'
        | 'assignedPageNumber'
      >
    >
  ) => void;
  /** Accept a held page WITHOUT a QR — operator assigns the slot manually. */
  acceptItem: (localId: string, pageNumber: number) => void;
  removeItem: (localId: string) => void;
  clear: () => void;
}

function makeLocalId(): string {
  return randomId();
}

function revoke(url: string | undefined) {
  if (url) {
    try {
      URL.revokeObjectURL(url);
    } catch {
      // ignore — already revoked or unsupported
    }
  }
}

export const useQuarantineStore = create<QuarantineState>()((set) => ({
  items: [],

  addItems: (inputs) =>
    set((state) => ({
      items: [
        ...state.items,
        ...inputs.map<QuarantineItem>((input) => ({
          localId: makeLocalId(),
          blob: input.blob,
          objectUrl: URL.createObjectURL(input.blob),
          qrData: input.qrData,
          qrBbox: input.qrBbox,
          classification: input.classification,
          resolution: 'pending'
        }))
      ]
    })),

  updateItem: (localId, patch) =>
    set((state) => ({
      items: state.items.map((item) => {
        if (item.localId !== localId) return item;
        // Replacing the bytes → swap the object URL so the thumbnail refreshes
        // and the old blob URL doesn't leak.
        if (patch.blob && patch.blob !== item.blob) {
          revoke(item.objectUrl);
          return {
            ...item,
            ...patch,
            objectUrl: URL.createObjectURL(patch.blob)
          };
        }
        return { ...item, ...patch };
      })
    })),

  acceptItem: (localId, pageNumber) =>
    set((state) => ({
      items: state.items.map((item) =>
        item.localId === localId
          ? { ...item, resolution: 'accepted', assignedPageNumber: pageNumber }
          : item
      )
    })),

  removeItem: (localId) =>
    set((state) => {
      const target = state.items.find((item) => item.localId === localId);
      revoke(target?.objectUrl);
      return { items: state.items.filter((item) => item.localId !== localId) };
    }),

  clear: () =>
    set((state) => {
      for (const item of state.items) revoke(item.objectUrl);
      return { items: [] };
    })
}));
